import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { reportApi } from '../../../services/reportService';
import { DateRange } from './StatsDateRange';
import {
  SectionCard,
  formatPLN,
  formatNumber,
  useAsyncData,
  StatsLoader,
  EmptyHint,
  StatsRow,
  MetricToggle,
} from './statsShared';
import { PeakHeatmap } from './PeakHeatmap';

interface Props {
  salonId: string;
  range: DateRange;
}

const DAY_NAMES: Record<string, string> = {
  Pn: 'Poniedziałek', Wt: 'Wtorek', Śr: 'Środa', Cz: 'Czwartek', Pt: 'Piątek', Sb: 'Sobota', Nd: 'Niedziela',
};
const pad = (h: number) => String(h).padStart(2, '0');
const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

export const PeakHoursTab: React.FC<Props> = ({ salonId, range }) => {
  const [metric, setMetric] = useState<'revenue' | 'bookings'>('revenue');
  const { data, loading, error } = useAsyncData(
    () => reportApi.getPeakHours(salonId, range.dateFrom, range.dateTo),
    [salonId, range.dateFrom, range.dateTo],
  );

  const hasData = !!data && data.matrix.some((c) => c.bookings > 0 || c.revenue > 0);

  return (
    <StatsLoader loading={loading} error={error} hasData={!!data} skeleton="block">
      {data && (
        <StatsRow template="3fr 2fr">
          <SectionCard
            title="Godziny szczytu"
            subtitle={metric === 'revenue' ? 'Przychód według dnia tygodnia i godziny' : 'Liczba wizyt według dnia tygodnia i godziny'}
            action={hasData ? <MetricToggle value={metric} onChange={setMetric} /> : undefined}
          >
            {hasData ? (
              <PeakHeatmap matrix={data.matrix} metric={metric} />
            ) : (
              <EmptyHint description="Wybierz dłuższy okres, żeby zobaczyć, kiedy salon ma najwięcej pracy.">Brak wizyt i sprzedaży w wybranym okresie</EmptyHint>
            )}
          </SectionCard>

          <SectionCard title="Najmocniejsze godziny" subtitle="Według przychodu" padding={data.topHours.length ? 'none' : 'normal'}>
            {data.topHours.length ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pora</TableCell>
                      <TableCell align="right">Wizyty</TableCell>
                      <TableCell align="right">Przychód</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.topHours.map((h) => (
                      <TableRow key={`${h.dayOfWeek}-${h.hour}`} hover>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                          {DAY_NAMES[h.dayLabel] || h.dayLabel},{' '}
                          <span style={{ ...NUM, whiteSpace: 'nowrap' }}>
                            {pad(h.hour)}:00–{pad(h.hour + 1)}:00
                          </span>
                        </TableCell>
                        <TableCell align="right" sx={NUM}>{formatNumber(h.bookings)}</TableCell>
                        <TableCell align="right" sx={{ ...NUM, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {formatPLN(h.revenue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyHint>Brak danych w wybranym okresie</EmptyHint>
            )}
          </SectionCard>
        </StatsRow>
      )}
    </StatsLoader>
  );
};
